import { useEffect, useState } from "react";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Activity, CheckCircle, XCircle, Clock, ChevronLeft, RefreshCw, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMonitoring() {
  const [uploads, setUploads] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => { loadData(); checkHealth(); }, []);

  const loadData = async () => {
    try {
      const snap = await getDocs(collection(db, "audioUploads"));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
      setUploads(items);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const checkHealth = async () => {
    try {
      const res = await fetch(`${API_URL}/api/health`);
      const data = await res.json();
      setHealth({ ok: res.ok, ...data });
    } catch (e) {
      setHealth({ ok: false, error: e.message });
    }
    setHealthLoading(false);
  };

  const deleteUpload = async (id) => {
    await deleteDoc(doc(db, "audioUploads", id));
    setUploads((u) => u.filter((x) => x.id !== id));
  };

  const stats = {
    total: uploads.length,
    complete: uploads.filter((u) => u.conversionStatus === "complete").length,
    failed: uploads.filter((u) => u.conversionStatus === "failed").length,
    processing: uploads.filter((u) => u.conversionStatus === "processing").length,
  };

  const STATUS_ICON = {
    complete: <CheckCircle size={13} className="text-[#00ff77]" />,
    failed: <XCircle size={13} className="text-red-400" />,
    processing: <Clock size={13} className="text-yellow-400 animate-pulse" />,
  };

  return (
    <div data-testid="admin-monitoring" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"><ChevronLeft size={16} /> Admin</Link>
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <Activity size={18} className="text-[#00ff77]" /> Monitoring
          </h2>
        </div>
        <button onClick={() => { setLoading(true); loadData(); }} className="text-zinc-500 hover:text-white p-2 rounded-xl hover:bg-white/5 transition-colors">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* API Health */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-4">
        <p className="text-zinc-500 text-xs font-semibold uppercase tracking-widest mb-2">API Health</p>
        {healthLoading ? (
          <div className="h-6 bg-[#1a1a1a] rounded-full w-32 animate-pulse" />
        ) : (
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${health?.ok ? "bg-[#00ff77]/10 text-[#00ff77] border border-[#00ff77]/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
            {health?.ok ? <CheckCircle size={14} /> : <XCircle size={14} />}
            {health?.ok ? "Backend Online" : "Backend Unreachable"}
          </div>
        )}
      </div>

      {/* Conversion Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total", value: stats.total, color: "text-white" },
          { label: "Complete", value: stats.complete, color: "text-[#00ff77]" },
          { label: "Failed", value: stats.failed, color: "text-red-400" },
          { label: "Processing", value: stats.processing, color: "text-yellow-400" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-[#111] border border-white/5 rounded-xl p-3 text-center">
            <p className={`text-2xl font-black ${color}`}>{value}</p>
            <p className="text-zinc-600 text-xs mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Recent Audio Uploads */}
      <div>
        <p className="text-zinc-500 text-xs font-semibold uppercase tracking-widest mb-3">Recent Audio Conversions</p>
        {loading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-[#111] rounded-xl animate-pulse" />)}</div>
        ) : uploads.length === 0 ? (
          <p className="text-zinc-600 text-sm py-6 text-center">No audio upload records yet.</p>
        ) : (
          <div className="space-y-1.5">
            {uploads.slice(0, 30).map((u) => (
              <div key={u.id} data-testid={`upload-row-${u.id}`}
                className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl px-3 py-2.5">
                {STATUS_ICON[u.conversionStatus] || STATUS_ICON.processing}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{u.title || u.originalFilename || "Untitled"}</p>
                  <p className="text-zinc-600 text-[10px]">
                    {u.creatorId?.slice(0, 8)}… · {u.durationSeconds ? `${Math.round(u.durationSeconds / 60)}min` : "—"} · {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                  </p>
                </div>
                <span className={`text-[10px] font-bold capitalize px-2 py-0.5 rounded-full border ${
                  u.conversionStatus === "complete" ? "text-[#00ff77] border-[#00ff77]/20 bg-[#00ff77]/10" :
                  u.conversionStatus === "failed" ? "text-red-400 border-red-400/20 bg-red-400/10" :
                  "text-yellow-400 border-yellow-400/20 bg-yellow-400/10"
                }`}>{u.conversionStatus || "pending"}</span>
                <button onClick={() => deleteUpload(u.id)} data-testid={`delete-upload-${u.id}`}
                  className="text-zinc-600 hover:text-red-400 p-1 rounded hover:bg-red-400/10 transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
